import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import type { ReactNode } from 'react';
import adaEmbed from '@ada-support/embed2';
import type { LooseObjectType } from 'utils/globalTypes';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { getFromBrandConfig } from 'brand-config';
import { logErrors } from 'utils';
import {
  selectBrandCode,
  selectIsBrandDetailsExist,
} from 'services/brand/brandSelectors';
import { selectUserSignInStatus } from 'services/session/sessionSelectors';
import { fetchThinSessionDetails } from 'services/session/sessionAsync';
import { selectOfferPlanCode } from 'services/offer/offerSelectors';

type WindowInfo = {
  hasActiveChatter: boolean;
  hasClosedChat: boolean;
  isChatOpen: boolean;
  isDrawerOpen: boolean;
};

type AdaChatProviderProps = {
  children: ReactNode;
};

const AdaChatContext = createContext({
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  openAdaChat(campaignName: string, payload?: LooseObjectType) {},
  isAdaChatAvailable: false,
});

function AdaChatProvider({ children }: AdaChatProviderProps) {
  const dispatch = useAppDispatch();
  const brandCode = useAppSelector(selectBrandCode);
  const planCode = useAppSelector(selectOfferPlanCode);
  const isBrandDetailsExist = useAppSelector(selectIsBrandDetailsExist);
  const isUserLogin = useAppSelector(selectUserSignInStatus);

  /* Not providing ADA Chat feature for eyeon users */
  const isAdaChatAvailable = useMemo(() => {
    if (!isBrandDetailsExist) {
      return false;
    }

    return getFromBrandConfig('adaChat');
  }, [brandCode, isBrandDetailsExist]);

  const showAdaChat = useCallback((action: boolean) => {
    adaEmbed.getInfo().then((windowInfo: WindowInfo) => {
      if (windowInfo.isDrawerOpen !== action) {
        adaEmbed.toggle();
      }
    });
  }, []);

  useEffect(() => {
    if (isAdaChatAvailable) {
      adaEmbed.start({
        handle: 'pango-gr',
        adaReadyCallback: () => {
          showAdaChat(false);
        },
        toggleCallback(isDrawerOpen: boolean) {
          /* Fetch user thin session details, if user login and pass it to the Ada */
          if (isDrawerOpen && isUserLogin) {
            dispatch(fetchThinSessionDetails())
              .then((response) => {
                if (response?.accessToken) {
                  adaEmbed.setSensitiveMetaFields({
                    Ada_JWT_Token_Captured: response?.accessToken,
                  });
                }
              })
              .catch((error) => {
                logErrors(error);
              });
          }
        },
      });
    }
  }, [isAdaChatAvailable]);

  const openAdaChat = useCallback(
    (campaignName: string, payload?: LooseObjectType) => {
      if (isAdaChatAvailable) {
        adaEmbed.setMetaFields({
          brand: brandCode,
          planCode: planCode ?? null,
          pageName: window.location.pathname,
          ...(payload ?? {}),
        });

        adaEmbed.triggerCampaign(campaignName, {
          ignoreStatus: false,
          ignoreFrequency: false,
        });

        showAdaChat(true);
      }
    },
    [planCode, isAdaChatAvailable, brandCode],
  );

  const value = useMemo(
    () => ({
      openAdaChat,
      isAdaChatAvailable,
    }),
    [isAdaChatAvailable, openAdaChat],
  );

  return (
    <AdaChatContext.Provider value={value}>{children}</AdaChatContext.Provider>
  );
}

export const useAdaChat = () => useContext(AdaChatContext);

export default AdaChatProvider;
